/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';
import { matchPath } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { isRouteEnabled } from '../../../constants/routes';
import { getLevelUserRoles, getInitiativeUsage } from '../../../selectors/initiative';
import { getPortfolioUsage, getUserPortfolioPermissionTags } from '../../../selectors/portfolio';
import Loader from '../../loader';
import { AppRouteInterface, RouteInterface } from '../../../types/routes';
import { generateUrl } from '../../../routes/util';
import { RootState } from '../../../reducers';
import { selectMostRecentSurvey } from '../../../slice/initiativeSurveyListSlice';
import { NavSelect } from './NavSelect';
import { SingleValue } from 'react-select';
import { UserRoles } from '../../../constants/user';
import './styles.scss';

const ACCESS_RESTRICTION_TOOLTIP = 'Unable to access with current permissions';

interface NavProps extends PropsFromRedux {
  appRoute: AppRouteInterface;
  userLevelRoles: UserRoles[];
  portfolioUserPermissionTags: UserRoles[];
  initiativeUsage: string[];
  portfolioUsage: string[];
}

interface NavState {
  saving: boolean;
  isPublic: boolean;
}

export interface NavSelectOption extends RouteInterface {
  value: string;
}

class Nav extends Component<RouteComponentProps<{ [key: string]: string }> & NavProps, NavState> {
  state = {
    saving: false,
    isPublic: false,
  };

  render() {
    const { appRoute } = this.props;
    const { routes = [] } = appRoute;

    if (routes.length <= 1) {
      return <></>;
    }

    const renderedRoutes: NavSelectOption[] = routes
      .filter((route) => !route.hidden && (!route.hideOnMissingPermission || this.hasPerm(route)))
      .map((route) => ({ ...route, value: route.label }));

    const onSelectRoute = (route: SingleValue<NavSelectOption>) => {
      const { url } = this.getUrl(route as NavSelectOption);
      this.props.history.push(url);
    };

    const isDisabled = (route: NavSelectOption) => {
      const { validUrl } = this.getUrl(route);
      return !this.hasPerm(route) || !validUrl;
    };

    return (
      <div className='nav-outer d-flex align-items-end'>
        <div className='d-none d-lg-block nav-wrapper flex-grow-1'>
          <nav className='nav'>{renderedRoutes.map(this.renderNavLink)}</nav>
        </div>
        <div className='d-block d-lg-none align-self-center'>
          <NavSelect onSelectRoute={onSelectRoute} renderedRoutes={renderedRoutes} isOptionDisabled={isDisabled} />
        </div>
        {this.state.saving && <Loader />}
      </div>
    );
  }

  getUrl(route: RouteInterface) {
    const urlObj = {
      validUrl: true,
      url: '',
    };
    const { match, initiativeSurveyListState, currentSurvey } = this.props;
    const params = { ...route.defaultParams, ...match.params };
    if (
      initiativeSurveyListState.loaded &&
      initiativeSurveyListState.initiativeId === match.params.initiativeId &&
      currentSurvey &&
      !params.surveyId
    ) {
      params.surveyId = currentSurvey._id;
    }

    try {
      urlObj.url = generateUrl(route, params);
    } catch {
      urlObj.validUrl = false;
    }

    return urlObj;
  }

  hasPerm(route: RouteInterface) {
    const { match, userLevelRoles, portfolioUserPermissionTags, initiativeUsage, portfolioUsage } = this.props;
    const isPortfolioTracker = !!match.params.portfolioId;

    const permissionTags = isPortfolioTracker ? portfolioUserPermissionTags : userLevelRoles;
    const usage = isPortfolioTracker ? portfolioUsage : initiativeUsage;
    const [initiativePerm, userPerm] = isRouteEnabled(route, permissionTags, usage);

    return initiativePerm && userPerm;
  }

  renderNavLink = (route: RouteInterface) => {
    const key = `navlink-${route.id}`;

    //Check that there is a role array defined in the routes, and the user has one of those roles,
    //and also check that the intiaitive/project/etc has a usage defined, and this component is enabled according to route too
    const permClass = route.navItemPermissionId ? `nav_${route.navItemPermissionId}` : '';
    const routeClass = `${permClass} ${route.id}`;

    const { url, validUrl } = this.getUrl(route);

    const weglotHack = <div className='d-none' />;

    if (!this.hasPerm(route) || !validUrl) {
      return (
        <NavLink
          key={key}
          exact={route.exact !== false}
          onClick={(e) => e.preventDefault()}
          title={ACCESS_RESTRICTION_TOOLTIP}
          to='/'
          className={`nav-disabled ${routeClass}`}
        >
          {route.label}
          {weglotHack}
        </NavLink>
      );
    }

    return (
      <NavLink
        exact={route.exact ?? false}
        key={key}
        title={route.tooltip}
        className={routeClass}
        activeClassName='active'
        to={url}
        isActive={() => !!matchPath(this.props.location.pathname, route)}
      >
        {route.label}
      </NavLink>
    );
  };
}

const mapStateToProps = (state: RootState) => ({
  initiativeUsage: getInitiativeUsage(state),
  userLevelRoles: getLevelUserRoles(state),
  portfolioUserPermissionTags: getUserPortfolioPermissionTags(state),
  portfolioUsage: getPortfolioUsage(state),
  initiativeSurveyListState: state.initiativeSurveyList,
  currentSurvey: selectMostRecentSurvey(state),
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(Nav));
